import { styled, Typography } from '@mui/material';
import Button from 'components/Button';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { WaterfallStructure } from 'further-types/lp/carry-settings';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { useDisclosure } from 'further-ui/hooks';
import {
  CalendarDay,
  getDistributionWaterfallElementValue,
  numberToCurrencyString,
} from 'further-ui/utils';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import api from 'lib/trpcClient';
import { round, sumBy } from 'lodash';
import { useState } from 'react';
import downloadXlxs from 'utils/downloadXlxs';
import {
  CapitalActionFormState,
  CapitalActionFormValues,
} from '../../../hooks/useCapitalActionForm';
import useSyncInitialDistributionValues from '../hooks/useSyncInitialDistributionValues';
import DistributableActionsHistory from './DistributableActionsHistory';
import OutsideOfCarryDistribution from './OutsideOfCarryDistribution';
import UploadManualOutputsModal, {
  FundingsRemainingToDistribute,
} from './UploadManualOutputsModal';
import WaterfallCalculations from './WaterfallCalculations';

const TextButton = styled(Button)({
  padding: 0,
});

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
  ledgerDate: CalendarDay;
};

const CapitalDistributionDetails: React.FC<Props> = ({
  vehicleId,
  ledgerDate,
  baseCurrency,
  index,
}) => {
  const [showCarriedInterestWaterfall, setShowCarriedInterestWaterfall] =
    useState(false);
  const uploadModalDisclosure = useDisclosure();

  const { vehicle } = useVehicle(vehicleId);

  const {
    formMethods: { watch, getValues, setValue, formState },
  } = useFormWithStateContext<
    CapitalActionFormValues,
    CapitalActionFormState
  >();

  const [fundsRemainingToDistribute] =
    api.capitalAction.getFundsRemainingToDistribute.useSuspenseQuery({
      vehicleId,
      date: ledgerDate,
      entries: getValues('entries').filter(
        (entry) =>
          entry.entryType !== CapitalActionEntryType.CapitalDistribution,
      ),
    });

  const exportCalculations =
    api.capitalAction.exportCalculationsFile.useMutation({
      onSuccess: (data) => {
        downloadXlxs(
          data.file,
          `Carried interest calculations - ${
            vehicle?.vehicleName
          } - ${ledgerDate.toLabel()}`,
        );
      },
    });

  const [
    fundsRemainingToDistributeOverrideValues,
    setFundsRemainingToDistributeOverrideValues,
  ] = useState<Array<FundingsRemainingToDistribute>>([]);

  const funds =
    fundsRemainingToDistributeOverrideValues.length > 0
      ? fundsRemainingToDistributeOverrideValues
      : fundsRemainingToDistribute;

  const { setTotalAmountAndCapitalBreakdown } =
    useSyncInitialDistributionValues({
      fundsRemainingToDistribute: funds,
      entryIndex: index,
    });

  const handleAmountChange = (
    commitmentId: string,
    amount: number,
    field:
      | 'subjectToCarry'
      | 'outsideOfCarry'
      | 'recallableOutsideOfCarry'
      | 'recallableSubjectToCarry',
  ) => {
    const newBreakdown =
      getValues(`entries.${index}.capitalBreakdown`)?.map((breakdownValue) => {
        if (breakdownValue.commitmentId === commitmentId) {
          const newValue = {
            subjectToCarry:
              breakdownValue.fundsDistributed?.subjectToCarry ?? 0,
            outsideOfCarry:
              breakdownValue.fundsDistributed?.outsideOfCarry ?? 0,
            recallableOutsideOfCarry:
              breakdownValue.fundsDistributed?.recallableOutsideOfCarry ?? 0,
            recallableSubjectToCarry:
              breakdownValue.fundsDistributed?.recallableSubjectToCarry ?? 0,
          };
          newValue[field] = amount;

          return {
            commitmentId: breakdownValue.commitmentId,
            fundsDistributed: newValue,
            amountLcy: round(
              newValue.subjectToCarry + newValue.outsideOfCarry,
              2,
            ),
          };
        }
        return breakdownValue;
      }) ?? [];

    setValue(`entries.${index}.capitalBreakdown`, newBreakdown);

    setValue(
      `entries.${index}.totalAmount`,
      round(sumBy(newBreakdown, 'amountLcy'), 2),
    );
  };

  const distributionValues = Object.fromEntries(
    (watch(`entries.${index}.capitalBreakdown`) ?? []).map((row) => [
      row.commitmentId,
      {
        total: row.fundsDistributed?.subjectToCarry ?? 0,
        recallable: row.fundsDistributed?.recallableSubjectToCarry ?? 0,
      },
    ]),
  );

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
    },
    {
      key: 'returnOfCapital',
      label: 'Return of capital',
    },
    {
      key: 'preferredReturn',
      label: 'Preferred return',
    },
    {
      key: 'extraProfits',
      label: 'Extra profits',
    },
    {
      key: 'totalDistributable',
      label: 'Total distributable',
    },
    {
      key: 'undistributed',
      label: 'Undistributed',
    },
    {
      key: 'distribution',
      label: 'Distribution',
      render: (row: { commitmentId: string }) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.total ?? 0}
          onChange={(value) => {
            handleAmountChange(row.commitmentId, value ?? 0, 'subjectToCarry');
          }}
          decimals={5}
          placeholder="Enter amount"
          currency={baseCurrency}
        />
      ),
    },
    {
      key: 'recallableDistribution',
      label: 'Of which recallable',
      render: (row: { commitmentId: string }, rowIndex: number) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.recallable ?? 0}
          onChange={(value) => {
            handleAmountChange(
              row.commitmentId,
              value ?? 0,
              'recallableSubjectToCarry',
            );
          }}
          decimals={5}
          placeholder="Enter amount"
          currency={baseCurrency}
          error={
            !!formState.errors.entries?.[index]?.capitalBreakdown?.[rowIndex]
              ?.fundsDistributed?.recallableSubjectToCarry
          }
        />
      ),
    },
  ];

  const rows = funds.map((details) => {
    const returnOfCapital = getDistributionWaterfallElementValue(
      details.waterfall.waterfallElements,
      WaterfallStructure.ReturnOfCapital,
    );

    const preferredReturn = getDistributionWaterfallElementValue(
      details.waterfall.waterfallElements,
      WaterfallStructure.PreferredReturn,
    );

    const extraProfits = getDistributionWaterfallElementValue(
      details.waterfall.waterfallElements,
      WaterfallStructure.ExcessProceeds,
    );

    return {
      id: details.commitmentId,
      commitmentId: details.commitmentId,
      investorName: details.investorLabel,
      returnOfCapital: numberToCurrencyString(returnOfCapital, {
        currency: baseCurrency,
      }),
      preferredReturn: numberToCurrencyString(preferredReturn, {
        currency: baseCurrency,
      }),
      extraProfits: numberToCurrencyString(extraProfits, {
        currency: baseCurrency,
      }),
      totalDistributable: numberToCurrencyString(
        details.waterfall.capitalSplit.limitedPartner.totalDistributable,
        {
          currency: baseCurrency,
        },
      ),
      undistributed: numberToCurrencyString(
        details.availableToDistributeSubjectToCarry,
        {
          currency: baseCurrency,
        },
      ),
    };
  });

  const handleExportCalculations = () => {
    exportCalculations.mutate({
      vehicleId,
      date: ledgerDate,
      entries: getValues('entries'),
    });
  };

  const handleManualOutputsUpload = (
    result: Array<FundingsRemainingToDistribute>,
  ) => {
    setTotalAmountAndCapitalBreakdown(result);
    setFundsRemainingToDistributeOverrideValues(result);
  };

  return (
    <Column spacing="sm">
      <Typography variant="h4">
        Process distribution (subject to carried interest)
      </Typography>
      <DistributableActionsHistory
        baseCurrency={baseCurrency}
        actionHistory={[]}
      />
      <Table columns={columns} rows={rows} />
      <Row centered justify="space-between">
        <TextButton
          variant="text"
          onClick={() =>
            setShowCarriedInterestWaterfall(!showCarriedInterestWaterfall)
          }
        >
          {showCarriedInterestWaterfall ? 'Hide' : 'Show'} carried interest
          waterfall calculation
        </TextButton>
        <Row spacing="sm" centered justify="space-between">
          <Button
            onClick={() => uploadModalDisclosure.onOpen()}
            variant="outlined"
            size="large"
          >
            {fundsRemainingToDistributeOverrideValues.length > 0
              ? 'Re-upload manual outputs'
              : 'Upload manual outputs'}
          </Button>
          {fundsRemainingToDistributeOverrideValues.length > 0 ? (
            <Button
              onClick={() => setFundsRemainingToDistributeOverrideValues([])}
              variant="contained"
              color="error"
              size="large"
            >
              Revert to system defaults
            </Button>
          ) : (
            <Button
              onClick={handleExportCalculations}
              loading={exportCalculations.isPending}
              variant="contained"
              size="large"
            >
              Download carried interest calculation audit sheet
            </Button>
          )}
        </Row>
      </Row>

      {showCarriedInterestWaterfall && (
        <WaterfallCalculations
          fundsToDistribute={funds}
          baseCurrency={baseCurrency}
        />
      )}
      <OutsideOfCarryDistribution
        fundsRemainingToDistribute={funds}
        baseCurrency={baseCurrency}
        index={index}
        onAmountChange={handleAmountChange}
      />

      <UploadManualOutputsModal
        open={uploadModalDisclosure.isOpen}
        onClose={uploadModalDisclosure.onClose}
        onUpload={handleManualOutputsUpload}
        date={ledgerDate}
        vehicleId={vehicleId}
      />
    </Column>
  );
};

export default CapitalDistributionDetails;
